/* eslint-disable no-magic-numbers */
import { QUERY_PARAMS, ROUTES, PREDICT_PRODUCT_SLUG } from '@futrli/shared-services';
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { UILoader } from '@futrli/ui-components';
import queryString from 'query-string';

import { useEmbedSignIn } from './use-embed-sign-in';

import { EMBED_API_ROUTES } from './constants';
import { fetchTokenApi, fetchDataSyncApi } from './use-sso-embed-api';
import { isUndefined } from 'lodash';
import { clearLocalStorageWithExceptions } from '@futrli/shared-services/string-utils/utils';

const DATA_SYNC_ENABLED = false;

/**
 * Decodes the payload portion of a JWT without verifying its signature. Used
 * only to read the `email`/`sub` claims out of the id-token returned by the
 * futrli-embed-agent — we are not relying on the decoded data for any
 * security decision (the token itself is what is dispatched downstream).
 */
const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const [, payload] = token.split('.');
  if (!payload) {
    return {};
  }
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '=='.slice((base64.length + 2) % 4);
  const json = atob(padded);
  return JSON.parse(decodeURIComponent(escape(json)));
};

/**
 * Automatic SSO Embed trigger by url.
 */
export const SSOEmbedTrigger: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const { handleEmbedSignIn, federatedSignInError } = useEmbedSignIn();

  useEffect(() => {
    clearLocalStorageWithExceptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Triggers the SSO flow as soon as the component is mounted.
   */
  useEffect(() => {
    const code = queryParams[QUERY_PARAMS.CODE] as string;
    const state = queryParams[QUERY_PARAMS.STATE] as string;
    const EMBED_AGENT_URL = window.__env__.EMBED_AGENT_URL;

    if (!code) {
      throw new Error('code needs to be defined');
    }

    if (!state) {
      throw new Error('state needs to be defined');
    }

    // Call the futrli-embed-agent with the code.
    // The agent microservice will return the access token.

    if (code) {
      fetchTokenApi(code)
        .then(response => {
          if (isUndefined(response)) {
            throw new Error(`Get access record response is undefined`);
          }

          const idTokenPayload = decodeJwtPayload(response.id_token);
          const accessTokenPayload = decodeJwtPayload(response.access_token);

          // Build an Amplify-v2-shaped user object from the tokens returned
          // by the embed agent. The downstream redux flow only reads
          // `username` / `attributes` / `signInUserSession.*.jwtToken`; it
          // never calls methods on the user, so a plain object is enough.
          // (Replaces the old `new CognitoUser(...).setSignInUserSession(...)`
          // construction from `amazon-cognito-identity-js`.)
          const user = {
            username: idTokenPayload.email,
            attributes: idTokenPayload,
            signInUserSession: {
              idToken: { jwtToken: response.id_token, payload: idTokenPayload },
              accessToken: { jwtToken: response.access_token, payload: accessTokenPayload },
              refreshToken: { token: '' }
            }
          };

          // FIXME: remove the hardcoded futrli-embed-idp
          handleEmbedSignIn(user, 'futrli-embed-idp', {
            originLocationPathname: [ROUTES.SSO_EMBED_USER, PREDICT_PRODUCT_SLUG].join('/'),
            originLocationSearch: location.search
          });
        })
        .catch(error => {
          throw new Error(`Get access record error: ${error}`);
        });
    }

    if (DATA_SYNC_ENABLED) {
      fetchDataSyncApi(`${EMBED_AGENT_URL}${EMBED_API_ROUTES.DATA_SYNC_REQUEST}?state=${state}`)
        .then(_response => {
          // TODO: update a status to say sync complete.
        })
        .catch(error => {
          throw new Error(`Data sync error: ${error}`);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles the federated sign in errors
   */
  React.useEffect(() => {
    if (!federatedSignInError) {
      return;
    }

    navigate(ROUTES.LOGIN);
  }, [federatedSignInError, navigate]);

  return <UILoader isFull={true} />;
};
